import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  children?: readonly SelectOption[];
}

interface MultiSelectProps {
  options: readonly SelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  countLabelFormatter?: (count: number) => string;
  single?: boolean;
  clearable?: boolean;
  className?: string;
  style?: React.CSSProperties;
  searchable?: boolean;
  size?: 'sm';
  tagDisplay?: boolean;
  disabled?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  onOpen?: () => void;
  onOpenChange?: (open: boolean) => void;
  loading?: boolean;
  loadingText?: string;
  errorText?: string;
  emptyText?: string;
  exclude?: boolean;
  onExcludeChange?: (exclude: boolean) => void;
  allowExcludeAll?: boolean;
  onSelectAll?: () => void | Promise<void>;
  dropdownFooter?: (close: () => void) => React.ReactNode;
  dropdownWidth?: number;
}

interface DropPos {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  minWidth: number;
  maxWidth: number;
}

const findOptionByValue = (options: readonly SelectOption[], target: string): SelectOption | undefined => {
  for (const option of options) {
    if (option.value === target) return option;
    if (option.children?.length) {
      const found = findOptionByValue(option.children, target);
      if (found) return found;
    }
  }
  return undefined;
};

const findOptionPath = (options: readonly SelectOption[], target: string, path: string[] = []): string[] | null => {
  for (const option of options) {
    const next = [...path, option.label];
    if (option.value === target) return next;
    if (option.children?.length) {
      const found = findOptionPath(option.children, target, next);
      if (found) return found;
    }
  }
  return null;
};

const collectOptionValues = (options: readonly SelectOption[]): string[] => {
  const values: string[] = [];
  for (const option of options) {
    values.push(option.value);
    if (option.children?.length) values.push(...collectOptionValues(option.children));
  }
  return values;
};

const filterOptionsTree = (options: readonly SelectOption[], keyword: string): SelectOption[] => {
  const q = keyword.trim().toLowerCase();
  if (!q) return [...options];
  return options
    .map((option) => {
      const selfMatched = option.label.toLowerCase().includes(q);
      const children = option.children?.length ? filterOptionsTree(option.children, keyword) : [];
      if (selfMatched) return { ...option };
      if (children.length) return { ...option, children };
      return null;
    })
    .filter((item): item is SelectOption => item !== null);
};

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '请选择',
  countLabelFormatter,
  single = false,
  clearable = false,
  className = '',
  style,
  searchable = false,
  size,
  tagDisplay = false,
  disabled = false,
  searchValue,
  onSearchChange,
  searchPlaceholder = '搜索...',
  onOpen,
  onOpenChange,
  loading = false,
  loadingText = '加载中...',
  errorText = '',
  emptyText = '暂无数据',
  exclude = false,
  onExcludeChange,
  allowExcludeAll = false,
  onSelectAll,
  dropdownFooter,
  dropdownWidth,
}) => {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState<DropPos | null>(null);
  const [search, setSearch] = useState('');
  const [tagVisibleCount, setTagVisibleCount] = useState<number>(Infinity);
  const ref = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const tagRowRef = useRef<HTMLDivElement>(null);
  const tagMeasureRef = useRef<HTMLDivElement>(null);
  const isDisabled = disabled || style?.pointerEvents === 'none';
  const closeDropdown = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && dropRef.current && !dropRef.current.contains(t)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [closeDropdown]);

  const calcPos = useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const dropH = 320;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const goUp = spaceBelow < dropH && spaceAbove > spaceBelow;
    const desiredWidth = Math.min(Math.max(r.width, dropdownWidth ?? r.width), window.innerWidth - 16);
    const goRight = r.left + desiredWidth > window.innerWidth - 8;
    const maxWidth = goRight ? r.right - 8 : window.innerWidth - r.left - 8;
    const panelWidth = Math.min(desiredWidth, maxWidth);
    setDropPos({
      minWidth: panelWidth,
      maxWidth: panelWidth,
      ...(goUp ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
      ...(goRight ? { right: window.innerWidth - r.right } : { left: r.left }),
    });
  }, [dropdownWidth]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      onSearchChange?.('');
      setDropPos(null);
      return;
    }
    calcPos();
    if (searchable && searchRef.current) searchRef.current.focus();
  }, [open, searchable, calcPos]);

  useEffect(() => {
    if (isDisabled) closeDropdown();
  }, [closeDropdown, isDisabled]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', calcPos, true);
    window.addEventListener('resize', calcPos);
    return () => {
      window.removeEventListener('scroll', calcPos, true);
      window.removeEventListener('resize', calcPos);
    };
  }, [open, calcPos]);

  useLayoutEffect(() => {
    if (!tagDisplay || !tagMeasureRef.current || !tagRowRef.current) return;
    if (value.length <= 1) {
      setTagVisibleCount(Infinity);
      return;
    }
    const containerW = tagRowRef.current.clientWidth;
    const tags = Array.from(tagMeasureRef.current.children) as HTMLElement[];
    const badgeW = 46;
    const gap = 4;
    let used = 0;
    let count = 0;
    for (let i = 0; i < tags.length; i++) {
      const tw = tags[i].offsetWidth + gap;
      const isLast = i === tags.length - 1;
      if (used + tw + (isLast ? 0 : badgeW) <= containerW) {
        used += tw;
        count++;
      } else {
        break;
      }
    }
    setTagVisibleCount(Math.max(1, count));
  }, [value.join(','), tagDisplay]);

  const getDisplayLabel = (v: string) => {
    const path = findOptionPath(options, v);
    if (!path?.length) return v;
    return path.length > 1 ? `${path[0]} / ${path[path.length - 1]}` : path[0];
  };

  const toggle = (v: string) => {
    if (isDisabled) return;
    const opt = findOptionByValue(options, v);
    if (opt?.disabled) return;
    if (single) {
      onChange([v]);
      setOpen(false);
      return;
    }
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  const label =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? `${exclude ? '排除：' : ''}${getDisplayLabel(value[0])}`
        : countLabelFormatter
          ? countLabelFormatter(value.length)
          : `${exclude ? '已排除' : '已选'} ${value.length} 项`;

  const suffix = (
    <span
      className={`ms-suffix${clearable && value.length > 0 ? ' ms-suffix--clearable' : ''}`}
      onClick={
        clearable && value.length > 0
          ? (e) => {
              e.stopPropagation();
              if (isDisabled) return;
              onChange([]);
            }
          : undefined
      }
      title={clearable && value.length > 0 ? '清除' : undefined}
    >
      {loading ? (
        <span className="ms-spinner" aria-label={loadingText} />
      ) : (
        <svg className="ms-icon-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12" style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      )}
      <svg className="ms-icon-clear" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </span>
  );

  const effectiveSearch = searchValue ?? search;
  const filteredOptions = filterOptionsTree(options, effectiveSearch);
  const selectableValues = collectOptionValues(filteredOptions).filter((value) => !(exclude && value === '全部' && !allowExcludeAll));
  const hasExcludableSelection = value.some((item) => allowExcludeAll || item !== '全部');

  const renderOption = (opt: SelectOption, depth = 0): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    const checked = value.includes(opt.value);
    const itemStyle = { paddingLeft: 12 + depth * 18 };

    if (single) {
      nodes.push(
        <div
          key={opt.value}
          className={`ms-item ms-item-single${checked ? ' selected' : ''}${opt.disabled ? ' ms-item-disabled' : ''}`}
          onClick={() => toggle(opt.value)}
          title={opt.disabled ? '无访问权限' : undefined}
          style={itemStyle}
        >
          <span style={{ flex: 1 }}>{depth > 0 ? `└ ${opt.label}` : opt.label}</span>
        </div>
      );
    } else {
      nodes.push(
        <label key={opt.value} className={`ms-item${checked ? ' selected' : ''}${opt.disabled ? ' ms-item-disabled' : ''}`} style={itemStyle}>
          <input type="checkbox" checked={checked} onChange={() => toggle(opt.value)} disabled={isDisabled || opt.disabled} />
          <span title={opt.label} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: depth === 0 && opt.children?.length ? 600 : 400 }}>
            {depth > 0 ? `└ ${opt.label}` : opt.label}
          </span>
        </label>
      );
    }

    if (opt.children?.length) {
      for (const child of opt.children) {
        nodes.push(...renderOption(child, depth + 1));
      }
    }
    return nodes;
  };

  return (
    <div className={`ms-wrap ${isDisabled ? 'ms-wrap-disabled ' : ''}${className}`} ref={ref} style={style}>
      <button
        type="button"
        className={`ms-trigger${open ? ' open' : ''}${size === 'sm' ? ' ms-trigger-sm' : ''}`}
        onClick={() => {
          if (isDisabled) return;
          setOpen((o) => {
            const next = !o;
            if (next) onOpen?.();
            onOpenChange?.(next);
            return next;
          });
        }}
        disabled={isDisabled}
      >
        {tagDisplay && !single && value.length > 0 ? (
          <div ref={tagRowRef} className="ms-tag-row" style={{ overflow: 'hidden', position: 'relative' }}>
            <div
              ref={tagMeasureRef}
              aria-hidden
              style={{ position: 'absolute', visibility: 'hidden', display: 'flex', gap: 4, flexWrap: 'nowrap', pointerEvents: 'none', top: 0, left: 0 }}
            >
              {value.map((v) => (
                <span key={v} className="ms-tag">
                  <span className="ms-tag-label">{getDisplayLabel(v)}</span>
                  <span className="ms-tag-remove">×</span>
                </span>
              ))}
            </div>
            {value.slice(0, tagVisibleCount === Infinity ? value.length : tagVisibleCount).map((v) => (
              <span key={v} className="ms-tag">
                <span className="ms-tag-label">{getDisplayLabel(v)}</span>
                <span className="ms-tag-remove" onClick={(e) => { e.stopPropagation(); if (!isDisabled) onChange(value.filter((x) => x !== v)); }}>×</span>
              </span>
            ))}
            {tagVisibleCount < value.length && <span className="ms-tag-overflow">+{value.length - tagVisibleCount}项</span>}
          </div>
        ) : (
          <span className="ms-label" title={label}>{label}</span>
        )}
        {suffix}
      </button>

      {open && dropPos && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className="ms-dropdown"
          style={{
            position: 'fixed',
            top: dropPos.top,
            bottom: dropPos.bottom,
            left: dropPos.left,
            right: dropPos.right,
            minWidth: dropPos.minWidth,
            maxWidth: dropPos.maxWidth,
          }}
        >
          {searchable && (
            <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
              <input
                ref={searchRef}
                type="text"
                value={effectiveSearch}
                onChange={(e) => {
                  const next = e.target.value;
                  if (searchValue === undefined) setSearch(next);
                  onSearchChange?.(next);
                }}
                placeholder={searchPlaceholder}
                style={{ width: '100%', padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, outline: 'none', background: 'var(--surface)', color: 'var(--text)' }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {!single && (
            <div className="ms-actions">
              {onExcludeChange ? (
                <>
                  <label><input type="checkbox" checked={selectableValues.length > 0 && selectableValues.every((item) => value.includes(item))} onChange={(event) => { if (isDisabled || loading) return; if (!event.target.checked) { onChange([]); return; } if (onSelectAll) { void onSelectAll(); return; } onChange(selectableValues); }} disabled={isDisabled || loading} /> 全选</label>
                  <label title={hasExcludableSelection ? undefined : '请先选择具体项'}><input type="checkbox" checked={exclude} onChange={(event) => !isDisabled && hasExcludableSelection && onExcludeChange(event.target.checked)} disabled={isDisabled || !hasExcludableSelection} /> 排除</label>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => { if (isDisabled || loading) return; if (onSelectAll) { void onSelectAll(); return; } onChange(collectOptionValues(filteredOptions)); }} disabled={isDisabled || loading}>全选</button>
                  <button type="button" onClick={() => !isDisabled && !loading && onChange([])} disabled={isDisabled || loading}>清除</button>
                </>
              )}
            </div>
          )}
          <div className="ms-list">
            {loading ? (
              <div className="ms-loading">
                <span className="ms-spinner" />
                <span>{loadingText}</span>
              </div>
            ) : errorText ? (
              <div className="ms-empty ms-empty-error">{errorText}</div>
            ) : filteredOptions.length === 0 ? (
              <div className="ms-empty">{emptyText}</div>
            ) : (
              filteredOptions.flatMap((opt) => renderOption(opt))
            )}
          </div>
          {dropdownFooter && (
            <div style={{ borderTop: '1px solid var(--border)' }}>
              {dropdownFooter(closeDropdown)}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default MultiSelect;
